import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { ArrowRight, Edit3, Droplets, Heart, FlaskConical, Stethoscope, Target, BarChart3, Zap } from 'lucide-react';
export function PresentationCard({
  state,
  prevStep,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const getIcon = () => {
    switch (role) {
      case 'Nephrologist':
        return Droplets;
      case 'Cardiologist':
        return Heart;
      case 'Endocrinologist':
        return FlaskConical;
      case 'PCP':
        return Stethoscope;
      default:
        return Droplets;
    }
  };
  const Icon = getIcon();
  return <div className="flex-grow flex flex-col pt-16 bg-gray-900 min-h-screen" data-id="element-365">
      <div className="flex-grow flex items-center justify-center p-6 md:p-8" data-id="element-366">
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.5
      }} className="w-full max-w-6xl bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col" data-id="element-367">
          {/* Header */}
          <div className="px-10 py-8 flex items-center justify-between border-b" style={{
          borderColor: `${color}30`,
          backgroundColor: `${color}08`
        }} data-id="element-368">
            <div className="flex items-center" data-id="element-369">
              <div className="p-4 rounded-2xl mr-6" style={{
              backgroundColor: color,
              color: 'white'
            }} data-id="element-370">
                <Icon size={44} data-id="element-371" />
              </div>
              <div data-id="element-372">
                <h1 className="text-4xl md:text-5xl font-black text-dark tracking-tight" data-id="element-373">
                  {role}s
                </h1>
                <p className="text-xl font-medium text-gray-500 mt-1" data-id="element-374">
                  {state.groupName || 'Your Group'}
                </p>
              </div>
            </div>
          </div>

          {/* Body — 3 strategic pillars + share-back */}
          <div className="p-8 md:p-10" data-id="element-375">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8" data-id="element-376">
              {/* A. Segmentation */}
              <div data-id="element-377">
                <div className="flex items-center mb-3" data-id="element-378">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{
                  backgroundColor: `${color}15`,
                  color
                }} data-id="element-379">
                    <Target size={18} data-id="element-380" />
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{
                  color
                }} data-id="element-381">
                    A. Segmentation & Targeting
                  </h3>
                </div>
                <p className="text-lg font-medium text-gray-800 leading-relaxed" data-id="element-382">
                  {state.cardSegmentation || '...'}
                </p>
              </div>

              {/* B. Features & Arguments */}
              <div data-id="element-383">
                <div className="flex items-center mb-3" data-id="element-384">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{
                  backgroundColor: `${color}15`,
                  color
                }} data-id="element-385">
                    <BarChart3 size={18} data-id="element-386" />
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{
                  color
                }} data-id="element-387">
                    B. Features & Arguments
                  </h3>
                </div>
                <p className="text-lg font-medium text-gray-800 leading-relaxed" data-id="element-388">
                  {state.cardFeatures || '...'}
                </p>
              </div>

              {/* C. Motivation */}
              <div data-id="element-389">
                <div className="flex items-center mb-3" data-id="element-390">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{
                  backgroundColor: `${color}15`,
                  color
                }} data-id="element-391">
                    <Zap size={18} data-id="element-392" />
                  </div>
                  <h3 className="text-xs font-bold uppercase tracking-widest" style={{
                  color
                }} data-id="element-393">
                    C. The Right Motivation
                  </h3>
                </div>
                <p className="text-lg font-medium text-gray-800 leading-relaxed" data-id="element-394">
                  {state.cardMotivation || '...'}
                </p>
              </div>
            </div>

            {/* Share-back quote */}
            <div className="border-t border-gray-100 pt-8" data-id="element-395">
              <div className="border-l-4 pl-6 py-2" style={{
              borderColor: color
            }} data-id="element-396">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2" data-id="element-397">
                  What others should know
                </p>
                <p className="text-2xl md:text-3xl font-bold text-dark italic leading-tight" data-id="element-398">
                  "{state.cardQuoteForOthers || '...'}"
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Controls */}
      <div className="p-6 flex justify-between items-center bg-gray-900 border-t border-gray-800" data-id="element-399">
        <button onClick={prevStep} className="inline-flex items-center px-6 py-3 text-white hover:text-gray-300 transition-colors" data-id="element-400">
          <Edit3 className="mr-2 w-5 h-5" data-id="element-401" />
          Edit Card
        </button>
        <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-3 text-base font-semibold text-gray-900 bg-white rounded-full hover:bg-gray-100 transition-colors" data-id="element-402">
          Continue to Comparison
          <ArrowRight className="ml-2 w-5 h-5" data-id="element-403" />
        </button>
      </div>
    </div>;
}